import type { FeatureData } from './FeaturePage';

/** Feature-page visuals, converted to WebP and served from /images/features. */
const featureImg = (n: number) => `/images/features/feature-${n}.webp`;
const howItWorksImg = (name: string) => `/images/features/how-it-works-${name}.webp`;

export const features: Record<string, FeatureData> = {

  'branded-tracking': {
    slug: 'branded-tracking',
    icon: 'ri-map-pin-user-line',
    name: 'Branded Tracking',
    accentColor: '#1769ff',
    accentColorLight: '#eef5ff',
    plan: 'Premium',
    liteFrom: 'Lite',
    tagline: 'Branded Tracking URL',
    headline: 'One tracking URL. Your logo. Live map.',
    headlineHighlight: 'Real-time delivery transparency',
    description: 'Give every customer a dedicated tracking page with your logo, live status, ETA, order progress timeline, and an interactive map showing the driver’s location — with planned vs actual route. Optional driver contact keeps delivery smooth and cuts "where is my order?" calls.',
    heroStats: [
      { value: '1 link', label: 'Per order, any device' },
      { value: 'Live', label: 'Map, ETA and status' },
      { value: '0', label: 'App installs for customers' },
    ],
    heroImage: {
      src: featureImg(22),
      alt: 'Branded Nilgiri Fresh tracking page on a phone showing arriving in 6 min, the order timeline, a live map of Bengaluru and the driver card',
      width: 941,
      height: 1672,
    },
    beforeAfter: {
      before: [
        'Customers call support to ask where their order is, and the dispatcher has to find the driver first.',
        'The only tracking you can offer is a carrier page with someone else’s logo on it.',
        'Nobody knows the ETA until the driver is already at the door.',
        'Ratings and complaints arrive days later, in an email, after the moment has passed.',
      ],
      after: [
        'Every order gets a tracking link with your logo, sent by SMS, email or WhatsApp at dispatch.',
        'Customers see the live map, the ETA and a step-by-step progress timeline themselves.',
        'A one-tap call to the driver handles “I’m at the back gate” without going through you.',
        'A rating prompt at delivery captures feedback while the handoff is fresh.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'The customer view',
        title: 'A live map and an honest ETA, on any phone',
        body: 'The tracking page opens in the browser, so there is nothing to install. Customers see the current status, the ETA, the driver on the map and a timeline from Order Confirmed through In Transit to Delivered.',
        bullets: [
          'Live vehicle position with Map and Satellite views',
          'Toggle planned route against the actual route driven',
          'Timeline with timestamps at every stage, including returns to hub',
        ],
        image: {
          src: featureImg(21),
          alt: 'Customer on a sofa checking a branded Geofleet tracking page that shows the driver arriving in five minutes',
          width: 1916,
          height: 821,
        },
      },
      {
        eyebrow: 'Feedback at the door',
        title: 'Ratings and comments, captured at the moment of delivery',
        body: 'Once the order is marked delivered, the same page asks the customer to rate the delivery and leave a note. Feedback lands against the order and the driver, so you can see patterns by category instead of guessing from a handful of emails.',
        bullets: [
          'Star rating and optional comment on the tracking page',
          'Feedback grouped by delivery quality, timeliness, driver service and tracking',
          'Low ratings surface in Reports & Analytics for follow-up',
        ],
        image: {
          src: featureImg(20),
          alt: 'Tracking page after delivery asking for a rating beside the Geofleet customer feedback dashboard with scores by category and a 30-day trend',
          width: 1672,
          height: 941,
        },
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-palette-line', title: 'Add your branding', description: 'Upload your logo and set colors. Each order gets a unique tracking URL (e.g. app.geofleet.ai/order/...) that shows only your brand.' },
      { step: '2', icon: 'ri-send-plane-line', title: 'Share the link', description: 'Send the tracking link via SMS, email, or WhatsApp. Customers open it on any device — no app install required.' },
      { step: '3', icon: 'ri-map-pin-user-line', title: 'They see everything live', description: 'Status, ETA, order progress timeline (Confirmed → In Transit → Delivered), driver name with contact, and a live map with planned vs actual route.' },
    ],
    capabilities: [
      { icon: 'ri-palette-line', title: 'Your logo & branding', description: 'Tracking page shows your logo and brand — no generic carrier look. Optional custom domain (e.g. track.yourbrand.com).' },
      { icon: 'ri-time-line', title: 'Status & ETA', description: 'Clear labels like "In Transit" and a prominent ETA (date and time) so customers know exactly when to expect delivery.' },
      { icon: 'ri-progress-4-line', title: 'Order tracking progress', description: 'Visual timeline: Order Confirmed → In Transit → Return to Hub → Delivered, with timestamps at each step.' },
      { icon: 'ri-map-pin-line', title: 'Live map & routes', description: 'Interactive map (Map or Satellite) with live vehicle position. Toggle planned route vs actual route for full transparency.' },
      { icon: 'ri-user-line', title: 'Driver info & contact', description: 'Show driver name and one-tap call so customers can coordinate handoff without calling your support line.' },
      { icon: 'ri-smartphone-line', title: 'Mobile-first tracking URL', description: 'Dedicated tracking page built for mobile: fast load, touch-friendly, works in any browser — no app required.' },
      { icon: 'ri-notification-3-line', title: 'Multi-channel link sharing', description: 'Send tracking links via SMS, email, or WhatsApp automatically at dispatch and at key milestones.' },
      { icon: 'ri-star-smile-line', title: 'Delivery rating', description: 'Customers rate the delivery and leave a comment on the tracking page once the order is delivered.' },
      { icon: 'ri-bar-chart-box-line', title: 'Tracking analytics', description: 'Page views, engagement, and conversion from tracking page to repeat orders.', tag: 'AI' },
    ],
    useCases: [
      { industry: 'restaurants', title: 'Hot food, no “where is it?” calls', description: 'Customers watch the driver approach instead of calling the store during the rush.' },
      { industry: 'ecommerce', title: 'Your brand on the last mile', description: 'Own the post-purchase experience instead of handing it to a carrier page.' },
      { industry: 'flowers', title: 'Recipient tracking for gifts', description: 'Share the link with the recipient so timed deliveries land when someone is home.' },
    ],
    benefits: [
      { value: 'Fewer', label: 'Support calls', description: 'Live ETA and driver contact answer “where is my order?” before anyone has to ring you.' },
      { value: '1 URL', label: 'Per order', description: 'Each delivery gets a unique, shareable tracking URL — simple for customers and support.' },
      { value: '0', label: 'App installs needed', description: 'Customers open the tracking page in any browser — live map, ETA and driver contact, nothing to download.' },
    ],
    integrations: ['Shopify', 'WooCommerce', 'Toast', 'Square', 'Twilio', 'WhatsApp', 'Mailchimp'],
    faq: [
      { question: 'Can I use my own domain for the tracking URL?', answer: 'Yes. You can use a custom subdomain like track.yourdomain.com so every link is fully on your brand.' },
      { question: 'What does the customer see on the tracking page?', answer: 'Your logo, current status (e.g. In Transit), ETA, a step-by-step order progress timeline, driver name with call option, and a live map with planned and actual route.' },
      { question: 'Do customers need to download an app?', answer: 'No. The tracking page is a responsive web page — one link works in any browser on mobile or desktop.' },
      { question: 'When is the tracking link sent?', answer: 'Automatically at dispatch, and again at the milestones you choose, such as “out for delivery” or “arriving soon”. Links go out by SMS, email or WhatsApp depending on your notification settings.' },
      { question: 'Can customers contact the driver from the page?', answer: 'Yes, if you enable it. The page shows the driver’s name and a one-tap call button. You can turn driver contact off for operations that prefer all contact to go through support.' },
      { question: 'Which plan includes branded tracking?', answer: 'Every Geofleet Lite plan includes white-label tracking pages under your own brand. On Geofleet Command, branded tracking pages are included from Premium AI, and Professional includes live ETAs on a Geofleet-branded page.' },
    ],
    relatedPosts: [
      'white-label-delivery-tracking-custom-tracking-url-and-terminology',
      'delivery-notification-sms-templates-that-reduce-failed-deliveries',
      'real-cost-of-failed-deliveries',
    ],
  },

  'route-planner': {
    slug: 'route-planner',
    icon: 'ri-route-line',
    name: 'Route Planner',
    accentColor: '#00A67E',
    accentColorLight: '#E6F9F3',
    plan: 'Professional',
    liteFrom: 'Lite Plus',
    tagline: 'AI Route Optimization',
    headline: 'Deliver more stops in less time with',
    headlineHighlight: 'AI-optimized routes',
    description: 'Our machine learning engine analyzes millions of route permutations in seconds, factoring in traffic, time windows, vehicle capacity, and delivery priorities.',
    heroStats: [
      { value: '< 5s', label: 'To plan 100+ stops' },
      { value: '95%', label: 'On-time delivery rate' },
      { value: '20–30%', label: 'Lower delivery cost' },
    ],
    heroImage: {
      src: howItWorksImg('optimize-routes'),
      alt: 'Geofleet route planner showing four optimized multi-stop routes on a map with stops, distance and duration per driver',
      width: 1563,
      height: 1006,
    },
    beforeAfter: {
      before: [
        'A dispatcher spends the first hour of every shift sequencing stops by hand in a spreadsheet.',
        'Routes ignore time windows, so drivers arrive at closed stores and come back later.',
        'One new urgent order means re-planning the whole day for three drivers.',
        'Nobody can say whether the route the driver took was the route that was planned.',
      ],
      after: [
        'Orders flow in from your store, API or a CSV and the plan is ready in seconds.',
        'Time windows, vehicle capacity and shift limits are respected automatically.',
        'New orders slot into existing routes, with a re-optimisation only where it helps.',
        'Planned against actual route, distance and time are visible for every trip.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'Plan',
        title: 'Planning profiles for every hub, day and order type',
        body: 'Set the rules once: which vehicles are available, how long a stop takes, which orders take priority and how late is too late. Smart Planning then builds routes for tomorrow’s deliveries from the profile you pick, so Monday’s rules and Saturday’s rules can differ without anyone re-entering them.',
        bullets: [
          'Profiles per hub, day of week or order type, selected in one click',
          'Service times, vehicle capacity and shift limits as hard constraints',
          'Routes for 100+ stops across multiple drivers in under 5 seconds',
        ],
        image: {
          src: '/images/how-it-works/optimize-routes.png',
          alt: 'Smart Planning screen in Geofleet with the Tomorrow Deliveries planning profile selected and a Generate Smart Plan button',
          width: 2048,
          height: 406,
        },
      },
      {
        eyebrow: 'Adjust',
        title: 'Drag a stop, save, and the ETAs update',
        body: 'Optimised is a starting point, not a cage. Drag rows to resequence a route, watch the map and the per-stop ETAs change, then save or discard. Dispatchers keep local knowledge in the plan without fighting the optimiser.',
        bullets: [
          'Drag-and-drop resequencing with live ETA and transit-time per stop',
          'Map and Satellite views of the route as you edit it',
          'Unsaved changes are flagged until you commit them',
        ],
        image: {
          src: howItWorksImg('route-adjustments'),
          alt: 'Geofleet orders panel with drag-and-drop resequencing beside a satellite map of the route with numbered stops',
          width: 1376,
          height: 768,
        },
      },
      {
        eyebrow: 'Dispatch',
        title: 'Publish and the route is in the driver’s hands',
        body: 'One click publishes a planned trip to the driver app with the stop list, distance and expected duration. Drivers get turn-by-turn navigation; dispatchers see the trip go live and can renew or reassign it later.',
        bullets: [
          'Publish a trip to one driver or many at once',
          'Trip summary with orders, distance and planned duration',
          'Renew a trip to re-run it with the same stops tomorrow',
        ],
        image: {
          src: howItWorksImg('dispatch-to-driver'),
          alt: 'A trip published from the Geofleet dashboard arriving in the driver app with today’s deliveries and a Start route button',
          width: 1564,
          height: 1006,
        },
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-upload-line', title: 'Import your stops', description: 'Upload orders via CSV, API, or let them flow in automatically from your integrations.' },
      { step: '2', icon: 'ri-brain-line', title: 'AI optimizes routes', description: 'Our engine builds the most efficient routes considering traffic, time windows, and capacity.' },
      { step: '3', icon: 'ri-send-plane-line', title: 'Dispatch to drivers', description: 'One-click dispatch sends optimized routes directly to the driver app with turn-by-turn nav.' },
    ],
    capabilities: [
      { icon: 'ri-brain-line', title: 'ML route engine', description: 'Analyzes millions of permutations per second for globally optimal multi-stop routes.', tag: 'AI' },
      { icon: 'ri-traffic-light-line', title: 'Live traffic routing', description: 'Real-time traffic data adjusts routes dynamically as conditions change.' },
      { icon: 'ri-time-line', title: 'Time-window constraints', description: 'Respect customer delivery windows, store hours, and driver shift limits.' },
      { icon: 'ri-truck-line', title: 'Vehicle capacity planning', description: 'Factor in weight, volume, and item count per vehicle for optimal loading.' },
      { icon: 'ri-refresh-line', title: 'Dynamic re-routing', description: 'Automatically re-optimize when new orders arrive or conditions change mid-route.', tag: 'AI' },
      { icon: 'ri-group-line', title: 'Multi-driver balancing', description: 'Distribute stops evenly across your fleet for balanced workloads.' },
      { icon: 'ri-map-2-line', title: 'Territory planning', description: 'Define zones and auto-assign drivers to geographic territories.' },
      { icon: 'ri-settings-4-line', title: 'Planning profiles', description: 'Different dispatch rules per hub, day or order type, applied with one click.' },
      { icon: 'ri-line-chart-line', title: 'Route analytics', description: 'Compare planned vs actual routes — distance, time, and cost per delivery.' },
    ],
    useCases: [
      { industry: 'b2b-distribution', title: 'Standing orders and customer windows', description: 'Weekly B2B runs with fixed delivery windows and dock constraints, planned in seconds.' },
      { industry: 'grocery', title: 'Dense residential routes', description: 'High stop counts per van with tight slots and perishables that cannot wait.' },
      { industry: 'automotive-parts', title: 'Trade-counter runs all day', description: 'Short, repeated routes from the branch where every extra minute costs a workshop.' },
    ],
    benefits: [
      { value: '20–30%', label: 'Lower delivery cost', description: 'Shorter, denser routes mean less fuel and fewer driver hours per delivery.' },
      { value: '95%', label: 'On-time delivery rate', description: 'Time-window-aware routing keeps deliveries inside the promised slot.' },
      { value: '< 5s', label: 'To plan the day', description: 'Routes for 100+ stops across multiple drivers, ready before the coffee is.' },
    ],
    integrations: ['Google Maps', 'Waze', 'HERE Maps', 'Shopify', 'WooCommerce', 'Toast'],
    faq: [
      { question: 'How fast does route optimization calculate?', answer: 'Routes for 100+ stops across multiple drivers are calculated in under 5 seconds.' },
      { question: 'Does it work with real-time traffic?', answer: 'Yes. Routes incorporate live traffic data and dynamically re-route drivers if conditions change.' },
      { question: 'Can I set delivery time windows?', answer: 'Absolutely. You can set per-order time windows and the optimizer will respect them while minimizing total route time.' },
      { question: 'Can dispatchers change an optimized route?', answer: 'Yes. Stops can be dragged into a new order, and the map, ETAs and transit times update as you edit. Changes stay unsaved until you commit them, so you can discard an experiment.' },
      { question: 'What happens when a new order arrives mid-day?', answer: 'It can be slotted into an existing route or trigger a re-optimisation of the affected routes only. Published trips are updated in the driver app.' },
      { question: 'Which plan includes route optimization?', answer: 'On Geofleet Lite, the route planner with one-click optimization is included from Lite Plus ($99 a month for one hub). On Geofleet Command, route optimization is included from Professional and dynamic routing from Premium AI.' },
    ],
    relatedPosts: [
      'ai-route-optimization-delivery',
      'planning-profiles-different-dispatch-rules-per-hub-day-or-order-type',
      'best-route-optimization-software-delivery-fleets',
    ],
  },

  'ai-agents': {
    slug: 'ai-agents',
    icon: 'ri-robot-2-line',
    name: 'AI Agents',
    accentColor: '#2563eb',
    accentColorLight: '#DBEAFE',
    tagline: 'AI agents for delivery operations',
    headline: 'AI agents that dispatch, reroute and',
    headlineHighlight: 'resolve exceptions',
    description:
      'Geofleet AI agents watch your live orders, drivers and routes, then assign work, recover late routes and keep customers informed. You decide what they do on their own, and every action is logged.',
    heroStats: [
      { value: '8', label: 'Agent roles' },
      { value: '3', label: 'Levels of autonomy' },
      { value: '100%', label: 'Actions logged' },
    ],
    heroImage: {
      src: '/images/features/ai-agents/agents-console.jpg',
      alt: 'Geofleet AI agents console showing each agent’s autonomy level, a live activity feed and today’s impact',
      width: 1586,
      height: 992,
    },
    howItWorks: [
      {
        step: '1',
        icon: 'ri-slideshow-line',
        title: 'Define logistics playbooks',
        description:
          'Set SLAs, hub rules, carrier tiers, escalation paths, and guardrails. Agents inherit your operating model — not a one-size template.',
      },
      {
        step: '2',
        icon: 'ri-flow-chart',
        title: 'Agents execute in the stack',
        description:
          'Allocation, reroutes, nudges, and notifications fire automatically from live orders, GPS, ETAs, and carrier capacity — no spreadsheet handoffs.',
      },
      {
        step: '3',
        icon: 'ri-pulse-line',
        title: 'Observe, audit, improve',
        description:
          'Every action is logged with context. Tune thresholds, approve overrides, and let models learn from outcomes across your network.',
      },
    ],
    capabilities: [
      {
        icon: 'ri-truck-line',
        title: 'Dispatch & allocation agent',
        description:
          'Assigns orders to drivers and carriers using proximity, capacity, skills, shift windows, cost, and SLA risk — continuously as the day evolves.',
        tag: 'AI',
      },
      {
        icon: 'ri-ship-line',
        title: 'Carrier matcher agent',
        description:
          'Selects and books the right carrier per lane, load, and service level; rebalances when delays or failures threaten the plan.',
        tag: 'AI',
      },
      {
        icon: 'ri-route-line',
        title: 'Route recovery agent',
        description:
          'Detects missed windows, traffic spikes, and new injections; triggers partial or full re-optimization without waiting for a planner.',
        tag: 'AI',
      },
      {
        icon: 'ri-alarm-warning-line',
        title: 'Exception containment agent',
        description:
          'Triages failed attempts, address issues, and hub bottlenecks — proposes reassignments, customer comms, or human escalation with context.',
        tag: 'AI',
      },
      {
        icon: 'ri-timer-line',
        title: 'SLA guardian agent',
        description:
          'Monitors promised ETAs and contractual cutoffs; prioritizes work and alerts teams before breaches, not after.',
        tag: 'AI',
      },
      {
        icon: 'ri-customer-service-2-line',
        title: 'Customer comms agent',
        description:
          'Proactive SMS/email/WhatsApp for delays, proof of delivery, and handoff — aligned with your brand and tone.',
        tag: 'AI',
      },
      {
        icon: 'ri-building-2-line',
        title: 'Hub load-balancing agent',
        description:
          'Shifts volume across hubs and sort windows when inbound spikes or labor constraints threaten throughput.',
        tag: 'AI',
      },
      {
        icon: 'ri-user-star-line',
        title: 'Driver coach agent',
        description:
          'Flags risky patterns (idle, rerate, safety) and nudges drivers with contextual coaching — not generic blasts.',
        tag: 'AI',
      },
      {
        icon: 'ri-file-shield-line',
        title: 'Audit & policy agent',
        description:
          'Enforces COD, POD, and compliance checks before release; surfaces anomalies for finance and ops in one trail.',
      },
      {
        icon: 'ri-dashboard-3-line',
        title: 'Command centre console',
        description:
          'Live view of agent decisions, queue depth, overrides, and health across hubs — built for control-tower operators.',
      },
      {
        icon: 'ri-history-line',
        title: 'Decision trace',
        description:
          'Immutable reasoning trail: what the agent saw, what it chose, and why — for disputes, audits, and continuous improvement.',
      },
      {
        icon: 'ri-hand-heart-line',
        title: 'Human-in-the-loop',
        description:
          'Low-confidence or high-risk moves route to dispatchers with suggested actions; agents learn from every approval or edit.',
      },
    ],
    benefits: [
      {
        value: 'Less',
        label: 'Manual dispatch work',
        description: 'Agents take routine allocation and rerouting so planners focus on exceptions.',
      },
      {
        value: 'Faster',
        label: 'Response to exceptions',
        description: 'Late drivers and failed attempts are flagged and fixed while there is still time to save the SLA.',
      },
      {
        value: 'One',
        label: 'Command centre',
        description: 'Drivers, carriers, hubs and customers stay in sync without a patchwork of tools.',
      },
    ],
    faq: [
      {
        question: 'How are Geofleet AI agents different from a generic AI chatbot?',
        answer:
          'They are operational agents wired into orders, routes, carriers, drivers, and SLAs. They take actions (assign, reroute, notify, escalate) with guardrails — not just answer questions in a widget.',
      },
      {
        question: 'Can dispatchers override agent decisions?',
        answer:
          'Yes. Any assignment or recommendation can be overridden. Overrides are logged and feed back into models so agents align with how your team actually runs logistics.',
      },
      {
        question: 'Do agents work across first mile, middle mile, and last mile?',
        answer:
          'Yes. Playbooks can span hub intake, linehaul, carrier legs, and last-mile delivery so the same command centre orchestrates the full shipment lifecycle where you operate it.',
      },
      {
        question: 'How do agents improve over time?',
        answer:
          'Outcomes (on-time, cost, rework, overrides) close the loop. Thresholds and models adapt within your governance rules — you stay in control of what can change automatically.',
      },
      {
        question: 'Which plans include AI agents?',
        answer:
          'On Geofleet Lite, AI Copilot and Autopilot are included in the Lite Autopilot plan for teams delivering from one hub. On Geofleet Command, AI auto-dispatch and auto-allocation are included from Premium AI, and the full logistics agent suite (carrier matching, exception containment, SLA guardian, extended comms) is on Enterprise. Talk to sales about your volume and regions.',
      },
    ],
  },

  'proof-of-delivery': {
    slug: 'proof-of-delivery',
    icon: 'ri-camera-line',
    name: 'Proof of Delivery',
    accentColor: '#EC4899',
    accentColorLight: '#FCE7F3',
    plan: 'Lite',
    tagline: 'Smart Proof of Delivery',
    headline: 'Verify every delivery with',
    headlineHighlight: 'AI-powered proof',
    description: 'Capture photos, e-signatures, OTP codes, and barcode scans in one tap. AI validates delivery quality and flags anomalies automatically.',
    heroStats: [
      { value: '1 tap', label: 'To capture proof' },
      { value: '4 types', label: 'Photo, signature, OTP, scan' },
      { value: 'Every', label: 'Record timestamped and geo-tagged' },
    ],
    heroImage: {
      src: featureImg(19),
      alt: 'Driver app capturing delivery photos, recipient signature and OTP, beside the completed proof of delivery record on a laptop with a Download e-POD button',
      width: 1448,
      height: 1086,
    },
    beforeAfter: {
      before: [
        'A customer says the parcel never arrived and the only evidence is the driver’s word.',
        'Photos live in the driver’s camera roll, if they were taken at all.',
        'Finance holds back a payout while someone hunts through WhatsApp for a signature.',
        'Blurry or wrong-door photos are discovered weeks later, during the dispute.',
      ],
      after: [
        'Every delivery closes with a photo, a signature, an OTP or a scan, chosen per order type.',
        'Proof is timestamped and geo-tagged and stored against the order automatically.',
        'Anyone with access finds the record by order number in seconds and exports it as a PDF.',
        'AI checks photo quality and placement before the driver drives off.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'Capture',
        title: 'Photos, signature and notes in one delivery flow',
        body: 'The driver app walks through the handoff in order: load, payment, proofs, review. Package photos, a handoff photo and a signature are each one tap, and the step cannot be skipped if your policy requires it.',
        bullets: [
          'Multiple photos per delivery, each with time and GPS attached',
          'Add image or add signature from the same screen',
          'Mandatory proof types configurable per order type or customer',
        ],
        image: {
          src: featureImg(18),
          alt: 'Driver app Proofs step with package photos, an Add image tile, an Add signature button and a delivery note field',
          width: 941,
          height: 1672,
        },
        fit: 'contain',
      },
      {
        eyebrow: 'Verify',
        title: 'OTP and signature for deliveries that need a named recipient',
        body: 'For high-value, age-restricted or contractual deliveries, the customer receives a one-time code by SMS or email and shares it with the driver. Combined with a signature and an optional photo ID, the handoff is tied to a person, not just a doorstep.',
        bullets: [
          'OTP sent to the customer, entered by the driver to complete the stop',
          'Signed-by name and optional note stored with the signature',
          'Photo ID capture and match for restricted deliveries',
        ],
        image: {
          src: featureImg(17),
          alt: 'Driver app Verify recipient screen with a six-digit OTP, delivery photos, the recipient signature, signed-by name and optional photo ID',
          width: 941,
          height: 1672,
        },
        fit: 'contain',
      },
      {
        eyebrow: 'Retrieve',
        title: 'Download the e-POD from the order, not from a group chat',
        body: 'Every proof record sits on the order in the operations view. Open the order, download the e-POD as a PDF, or bulk-export a day’s proofs for an audit, an insurance claim or a customer dispute.',
        bullets: [
          'Search by order number, date, driver or customer',
          'Download e-POD per order or in bulk',
          'Attempted and failed deliveries keep their photos and reason codes too',
        ],
        image: {
          src: featureImg(16),
          alt: 'Geofleet orders view with an order’s action menu open on Download e-POD, a PDF preview of the proof of delivery and a Bulk export proofs button',
          width: 1672,
          height: 941,
        },
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-camera-line', title: 'Driver captures proof', description: 'One-tap photo, signature, or OTP capture at the point of delivery.' },
      { step: '2', icon: 'ri-brain-line', title: 'AI validates delivery', description: 'Computer vision checks photo quality, package placement, and detects anomalies.' },
      { step: '3', icon: 'ri-archive-line', title: 'Stored & searchable', description: 'All proof is timestamped, geo-tagged, and stored for instant retrieval.' },
    ],
    capabilities: [
      { icon: 'ri-camera-line', title: 'Photo capture', description: 'High-res delivery photos with automatic timestamp and GPS coordinates.' },
      { icon: 'ri-edit-line', title: 'E-signature', description: 'Customer signs directly on the driver\'s phone for verified handoff.' },
      { icon: 'ri-lock-password-line', title: 'OTP verification', description: 'Customer receives a code and confirms delivery by sharing it with the driver.' },
      { icon: 'ri-barcode-line', title: 'Barcode scanning', description: 'Scan package barcodes at pickup and delivery for chain-of-custody tracking.' },
      { icon: 'ri-eye-line', title: 'AI photo validation', description: 'Computer vision detects blurry photos, wrong locations, and missing packages.', tag: 'AI' },
      { icon: 'ri-shield-user-line', title: 'ID verification', description: 'Photo ID capture and match for age-restricted or high-value deliveries.' },
      { icon: 'ri-search-line', title: 'Instant search', description: 'Find any proof record by order number, date, driver, or customer in seconds.' },
      { icon: 'ri-file-download-line', title: 'Bulk export', description: 'Export proof records as PDF reports for audits, insurance claims, or disputes.' },
      { icon: 'ri-wifi-off-line', title: 'Works offline', description: 'Proof is captured without signal and synced when the driver is back online.' },
    ],
    useCases: [
      { industry: 'pharmacies', title: 'Named-recipient handoffs', description: 'OTP, signature and ID checks for prescriptions that cannot be left at the door.' },
      { industry: 'medical-supplies', title: 'Chain of custody', description: 'Barcode scans at pickup and delivery, with photos at every transfer.' },
      { industry: 'building-supplies', title: 'Proof of offload', description: 'Site photos and a signature settle “it was never delivered” on bulky drops.' },
    ],
    benefits: [
      { value: 'Minutes', label: 'To settle a dispute', description: 'Pull the photo, signature and timestamp from the order instead of chasing the driver.' },
      { value: 'Every', label: 'Delivery has a record', description: 'Timestamped, geo-tagged proof on each order, including attempted and failed stops.' },
      { value: 'Before', label: 'The driver leaves', description: 'AI flags a blurry photo or a wrong location while there is still time to fix it.' },
    ],
    faq: [
      { question: 'What types of proof can be captured?', answer: 'Photos, e-signatures, OTP codes, barcode scans, and customer ID verification — all from the driver app.' },
      { question: 'How does AI validate photos?', answer: 'Our computer vision model checks for blur, darkness, correct location, and visible package — flagging issues before the driver leaves.' },
      { question: 'How long are proof records stored?', answer: 'All proof records are stored for 12 months by default. Enterprise plans offer extended retention up to 7 years.' },
      { question: 'Can I require different proof for different orders?', answer: 'Yes. Proof requirements are set per order type or customer, so a prescription can require OTP and signature while a grocery drop needs a photo only.' },
      { question: 'Does proof capture work without mobile signal?', answer: 'Yes. Photos, signatures and OTP entry work offline in the driver app and sync automatically when the device reconnects.' },
      { question: 'Which plan includes proof of delivery?', answer: 'Photo proof of delivery is included on every plan, including Geofleet Lite. Barcode scanning and ID verification are included from the Premium plan.' },
    ],
    relatedPosts: [
      'proof-of-delivery-legal-cx-infrastructure',
      'real-cost-of-failed-deliveries',
      'medical-courier-software-chain-of-custody-hipaa-stat-runs',
    ],
  },

  'auto-allocation': {
    slug: 'auto-allocation',
    icon: 'ri-flashlight-line',
    name: 'Auto Allocation',
    accentColor: '#F97316',
    accentColorLight: '#FFEDD5',
    plan: 'Premium',
    liteFrom: 'Lite Plus',
    tagline: 'Agent-run dispatch',
    headline: 'Orders matched to drivers in seconds by',
    headlineHighlight: 'AI agents working in the background',
    description: 'Run dispatch the way ride-hailing does. An always-on dispatch agent watches every incoming order, picks the best driver or carrier, offers the job in the driver app and moves on. Dispatchers only see what needs a human.',
    heroStats: [
      { value: '24/7', label: 'Agent watching the queue' },
      { value: 'Seconds', label: 'From order to offer' },
      { value: 'Exceptions', label: 'All a dispatcher sees' },
    ],
    heroImage: {
      src: '/images/features/ai-agents/agents-console.jpg',
      alt: 'Geofleet AI agents console with the dispatch and allocation agent assigning orders to drivers in the background',
      width: 1586,
      height: 992,
    },
    beforeAfter: {
      before: [
        'A dispatcher reads every new order and picks a driver by hand, all day.',
        'Drivers wait on a call or a WhatsApp message to know what is next.',
        'The nearest driver gets the job even when their load is full or their next window is at risk.',
        'When the fleet is full, partner carriers are called one by one.',
      ],
      after: [
        'A dispatch agent matches each order the moment it lands and offers it in the driver app.',
        'Drivers accept on the phone; a decline sends the job to the next best driver automatically.',
        'Capacity, vehicle type, skills, time windows and SLA risk all count, not just distance.',
        'Own fleet and partner carriers are weighed in the same decision, by cost and ETA.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'Match',
        title: 'An agent picks the driver, like a ride-hailing app picks yours',
        body: 'The dispatch agent runs continuously in the background. When an order arrives it filters out drivers who cannot take it, then scores the rest on detour, current load, lateness risk on their existing stops and load balance across the fleet. The best driver gets the offer in seconds. A driver 800 m away with a window at risk loses to one 3 km away with slack.',
        bullets: [
          'Hard constraints: capacity, vehicle type, skills, shift end',
          'Weighted score: detour, SLA risk, fairness across drivers',
          'Rules come from the hub’s Planning Profile, so each hub can differ',
        ],
        image: {
          src: featureImg(7),
          alt: 'Geofleet assignment panel ranking four drivers by distance, parcel load, vehicle, delivery window and score, with the best match 3.1 km away chosen over the nearest driver',
          width: 1672,
          height: 941,
        },
      },
      {
        eyebrow: 'Offer',
        title: 'Drivers accept on the phone, nobody waits on a call',
        body: 'The matched driver sees the job in the driver app with the pickup, drop, window and payout, and accepts it there. If they decline or go offline, the agent re-runs the match and the job moves to the next best driver. Dispatchers do not touch the happy path at all.',
        bullets: [
          'Job offer with pickup, drop, window and payout in the driver app',
          'Decline or offline triggers an instant re-match',
          'Partner carriers quoted alongside your fleet when it is full or cheaper',
        ],
        image: {
          src: howItWorksImg('dispatch-to-driver'),
          alt: 'An order matched by the dispatch agent arriving in the driver app with today’s deliveries and a Start route button',
          width: 1564,
          height: 1006,
        },
      },
      {
        eyebrow: 'Rebalance',
        title: 'Re-matched on every event, all day',
        body: 'A completed drop frees capacity. A driver going offline releases their stops. An SLA countdown crossing a threshold prompts the agent to look for a better driver before the order is late, not after. An assigned order only moves when the new option is materially better, so drivers and customers are not churned by marginal gains.',
        bullets: [
          'Event-driven: new order, completed drop, driver status, SLA risk',
          'Minimum-improvement threshold prevents reassignment churn',
          'Hub load-balancing and SLA guardian agents work alongside on Enterprise',
        ],
        image: {
          src: featureImg(5),
          alt: 'Geofleet Auto Allocation live events feed showing a new order assigned, a drop completed, a re-evaluation with no change, a driver going offline and two stops reassigned on the map',
          width: 1672,
          height: 941,
        },
      },
      {
        eyebrow: 'Escalate',
        title: 'Humans only see the exceptions',
        body: 'Orders nobody qualifies for go to a queue with a reason, a retry timer and an alert to the dispatch tower. High-value, high-COD or fragile orders can be proposed rather than assigned. When a dispatcher steps in, they search for the order or the driver, drag the order onto a driver on the list or the map, or open it and pick from the drivers the system ranks for it. Every decision, by agent or human, is logged with its score and the alternatives.',
        bullets: [
          'Search any order or driver by ID, name, customer or area',
          'Drag and drop an order onto a driver; the system suggests the best drivers, ranked, when you assign manually',
          'Switch the map to 2.5D or 3D to see vehicles, orders and buildings in context while you work',
          'Approval thresholds by order value, COD amount or tag, with a decision trace for every assignment',
        ],
        image: {
          src: featureImg(6),
          alt: 'Geofleet dispatcher controls for Auto Allocation with orders awaiting approval, an unassigned cold-chain order with a retry timer, per-hub toggles and an approval threshold',
          width: 1672,
          height: 941,
        },
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-inbox-line', title: 'Order lands', description: 'From your store, API, sheet or a dispatcher, the order enters the queue with its window, size and requirements. The agent is already watching.' },
      { step: '2', icon: 'ri-robot-line', title: 'Agent matches and offers', description: 'Eligible drivers are filtered and scored in seconds; carriers are quoted alongside. The best driver gets the job in the app and accepts it there.' },
      { step: '3', icon: 'ri-user-star-line', title: 'Dispatchers handle the rest', description: 'Declines re-match automatically. Only unassignable orders and ones above your approval threshold reach a person, who searches, drags the order onto a driver or picks from the ranked suggestions.' },
    ],
    capabilities: [
      { icon: 'ri-robot-line', title: 'Always-on dispatch agent', description: 'Watches the order queue around the clock and matches each order the moment it arrives.', tag: 'AI' },
      { icon: 'ri-brain-line', title: 'Scored matching', description: 'Capacity, vehicle type, skills, detour, time windows and SLA risk weighed together under each hub’s Planning Profile, not nearest-driver.', tag: 'AI' },
      { icon: 'ri-smartphone-line', title: 'Offer and accept in the app', description: 'Drivers see pickup, drop, window and payout and accept on the phone. A decline re-matches instantly.' },
      { icon: 'ri-timer-flash-line', title: 'Event-driven re-matching', description: 'Runs on each new order, completed drop, driver status change or SLA risk threshold.' },
      { icon: 'ri-truck-line', title: 'Fleet and carriers together', description: 'Third-party carriers are quoted in the same decision as your own drivers, by cost and ETA.' },
      { icon: 'ri-search-line', title: 'Search orders and drivers', description: 'Find any order or driver by ID, customer, name or area and see its status, position and assignment.' },
      { icon: 'ri-drag-move-2-line', title: 'Drag-and-drop assignment', description: 'Drag an order onto a driver on the list or the map. The system ranks the best drivers for it as you assign, and flags any constraint the pick violates.', tag: 'AI' },
      { icon: 'ri-building-3-line', title: '2.5D and 3D map mode', description: 'Tilt the live map into 2.5D or full 3D to see vehicles, orders and buildings in context. Switch back to flat 2D with one click.' },
      { icon: 'ri-shield-check-line', title: 'Approval thresholds', description: 'Orders above a value, COD amount or with a tag are proposed to a dispatcher instead of assigned.' },
      { icon: 'ri-history-line', title: 'Decision trace', description: 'Every match records the score, the alternatives and who approved it, for audit and tuning.' },
    ],
    useCases: [
      { industry: 'restaurants', title: 'Matched as the order is placed', description: 'Each order goes to the driver who can reach the kitchen and the customer on time, as prep finishes, with no one on the radio.' },
      { industry: 'couriers', title: 'On-demand jobs across hubs', description: 'Walk-in and API jobs are offered to drivers across hubs and shippers without a dispatcher per city.' },
      { industry: 'ecommerce', title: 'Fleet first, carrier when it pays', description: 'Own drivers take what they can; the rest goes to the partner carrier with the best cost and ETA.' },
    ],
    benefits: [
      { value: 'Seconds', label: 'From order to offer', description: 'The agent matches the moment the order lands, not when a dispatcher gets to it.' },
      { value: 'Zero', label: 'Calls on the happy path', description: 'Drivers accept jobs in the app. Dispatchers only hear about exceptions.' },
      { value: 'Scoped', label: 'Rollout', description: 'Start with one hub and one order type, review the agent’s decisions, and widen as trust grows.' },
    ],
    integrations: ['Driver App', 'AI Agents', 'Route Planner', 'Webhooks', 'REST API'],
    faq: [
      { question: 'Is this just nearest-driver assignment?', answer: 'No. Distance is one input. Current load, remaining capacity, vehicle type, skills, time-window fit, Planning Profile rules and SLA risk all feed the score, which is why the nearest driver is often not the one chosen.' },
      { question: 'What does the driver see?', answer: 'The job appears in the driver app with the pickup, drop, delivery window and payout. The driver accepts it there. If they decline or go offline, the agent re-matches the order to the next best driver.' },
      { question: 'What happens when no driver qualifies?', answer: 'The order goes to a visible queue with the reason, for example no cold-chain driver online at the north hub. You choose the fallback: retry on a timer, alert the dispatch tower, offer it to a partner carrier or prompt a manual assignment.' },
      { question: 'Can dispatchers still assign by hand?', answer: 'Yes, at any time. Search for the order or the driver, then drag the order onto a driver on the list or the map, or open the order and choose from the drivers the system ranks for it by distance, load, vehicle, window and SLA risk. Any constraint the manual choice violates is flagged, and every manual change is recorded next to the agent’s decisions.' },
      { question: 'Is there a 3D view of the fleet?', answer: 'Yes. The live map has a 2D, 2.5D and 3D mode. In 2.5D the map tilts so vehicles and stops sit against the streets; in 3D buildings are drawn in volume so a dispatcher can see which block, tower or loading bay a driver is at. Orders, driver positions, routes and drag-and-drop assignment all work in every mode.' },
      { question: 'Can we roll it out gradually?', answer: 'Yes. Enable the agent per hub, per order type or per shipper, and set approval thresholds so orders above a value, a COD amount or with a tag are proposed rather than assigned. Most teams start narrow and widen weekly.' },
      { question: 'Which plan includes auto allocation?', answer: 'On Geofleet Lite, auto-allocation for on-demand deliveries is included from Lite Plus, and AI Copilot and Autopilot are included in Lite Autopilot. On Geofleet Command, the dispatch and allocation agent is included from Premium AI, and the full logistics agent suite, including carrier matching, hub load-balancing and SLA guardian, is on Enterprise.' },
    ],
    relatedPosts: [
      'auto-allocation-explained-orders-assigned-without-a-dispatcher',
      'copilot-dispatch-tower-guardrails-automation',
      'from-manual-dispatch-to-ai-powered-automation-in-logistics',
    ],
  },

  'cash-management': {
    slug: 'cash-management',
    icon: 'ri-money-dollar-circle-line',
    name: 'Cash Management',
    accentColor: '#F59E0B',
    accentColorLight: '#FEF3C7',
    plan: 'Lite',
    tagline: 'Cash-on-Delivery Tracking',
    headline: 'Track every cash payment with',
    headlineHighlight: 'real-time reconciliation',
    description: 'Monitor cash-on-delivery collections in real-time, detect discrepancies instantly, and automate driver cash settlements — eliminating revenue leakage.',
    heroStats: [
      { value: 'Live', label: 'Cash on ground, per driver' },
      { value: 'Same day', label: 'Reconciliation and settlement' },
      { value: 'Instant', label: 'Discrepancy alerts' },
    ],
    heroImage: {
      src: featureImg(14),
      alt: 'Geofleet cash management dashboard showing cash on ground, driver and hub pending amounts, cash by hub and the day’s transactions',
      width: 1586,
      height: 992,
    },
    beforeAfter: {
      before: [
        'Drivers hand over a bundle of cash at the end of the shift and someone counts it against a printed list.',
        'A short deposit is noticed two days later and nobody remembers which order it was.',
        'Dispatch has no idea how much cash a driver is carrying at 3 pm.',
        'Accounting re-keys the day’s collections into Tally or QuickBooks by hand.',
      ],
      after: [
        'Each cash collection is recorded in the driver app at the stop, with order ID, amount and time.',
        'Cash on ground is live per driver and per hub, all day.',
        'A mismatch between expected and collected raises an alert immediately.',
        'End-of-shift reconciliation and the accounting export are generated, not typed.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'Collect',
        title: 'Cash is recorded at the stop, as part of the delivery',
        body: 'The payment step sits inside the delivery flow in the driver app. When a COD order is marked delivered the amount is recorded against it, and an OTP from the customer can be required before the payment step completes.',
        bullets: [
          'Amount, order ID and timestamp captured automatically on delivery',
          'Optional OTP confirmation before the payment step',
          'Digital receipt sent to the customer by SMS or email',
        ],
        image: {
          src: '/images/driver-app-otp.png',
          alt: 'Driver app OTP verification screen used to confirm handover before the payment step',
          width: 555,
          height: 1024,
        },
        fit: 'contain',
      },
      {
        eyebrow: 'Watch',
        title: 'Cash on ground, by driver and by hub, all day',
        body: 'The cash dashboard shows total cash outstanding, how much each driver is carrying and what each hub is holding. Cash limits per driver trigger alerts and can pause new COD assignments until a deposit is made.',
        bullets: [
          'Live balances per driver and per hub',
          'Cash-in-hand limits with alerts when reached',
          'Manual entries for deposits and adjustments, with an audit trail',
        ],
        image: {
          src: featureImg(13),
          alt: 'Geofleet cash on ground view listing each driver’s cash carried against their limit, with near-limit and COD paused flags, beside cash held per hub',
          width: 1672,
          height: 941,
        },
      },
      {
        eyebrow: 'Settle',
        title: 'Reconcile the shift and export to your books',
        body: 'At the end of the shift, collections are compared with orders and deposits automatically. Discrepancies are listed per driver with the orders behind them, and the settled totals export to your accounting system by CSV or API.',
        bullets: [
          'End-of-shift report comparing orders, collections and deposits',
          'Settlement history and outstanding balances per driver',
          'Export to Tally, QuickBooks, Zoho Books or any system via CSV or API',
        ],
        image: {
          src: '/images/features/cash-reconciliation.webp',
          alt: 'Geofleet shift reconciliation with expected, collected, deposited and outstanding cash per driver, one settlement flagged for review, and Export CSV and Export via API buttons',
          width: 1672,
          height: 941,
        },
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-money-dollar-circle-line', title: 'Driver collects cash', description: 'The app records each COD collection with amount, order ID, and timestamp.' },
      { step: '2', icon: 'ri-refresh-line', title: 'Real-time tracking', description: 'Dispatchers see live cash balances per driver throughout the day.' },
      { step: '3', icon: 'ri-checkbox-circle-line', title: 'Automated settlement', description: 'End-of-day reconciliation and settlement reports generated automatically.' },
    ],
    capabilities: [
      { icon: 'ri-wallet-3-line', title: 'Live cash balances', description: 'See how much cash each driver is carrying in real-time.' },
      { icon: 'ri-alarm-warning-line', title: 'Discrepancy alerts', description: 'AI flags mismatches between collected and expected amounts instantly.', tag: 'AI' },
      { icon: 'ri-file-list-3-line', title: 'Auto-reconciliation', description: 'End-of-shift reports comparing orders, collections, and deposits.' },
      { icon: 'ri-bank-line', title: 'Settlement tracking', description: 'Track driver deposits, outstanding balances, and settlement history.' },
      { icon: 'ri-receipt-line', title: 'Digital receipts', description: 'Customers get digital receipts for cash payments via SMS or email.' },
      { icon: 'ri-lock-line', title: 'Cash limits', description: 'Set maximum cash-in-hand limits per driver with alerts when reached.' },
      { icon: 'ri-pie-chart-line', title: 'Payment analytics', description: 'COD vs digital payment ratios, collection rates, and settlement trends.' },
      { icon: 'ri-code-s-slash-line', title: 'Accounting export', description: 'Export to Tally, QuickBooks, Zoho, or any accounting system via CSV/API.' },
    ],
    useCases: [
      { industry: 'ecommerce', title: 'COD at scale', description: 'Hundreds of cash orders a day across drivers and hubs, reconciled the same evening.' },
      { industry: 'water', title: 'Standing orders paid in cash', description: 'Recurring deliveries where small cash amounts add up and go missing quietly.' },
      { industry: 'fuel', title: 'High-value cylinder drops', description: 'Large cash collections with per-driver limits and deposits enforced during the shift.' },
    ],
    benefits: [
      { value: 'Live', label: 'Cash visibility', description: 'Every amount collected is tracked from the stop to the deposit, per driver and per hub.' },
      { value: 'Same day', label: 'Reconciliation', description: 'Automated reports replace hours of end-of-shift counting and spreadsheet work.' },
      { value: 'Instant', label: 'Discrepancy alerts', description: 'Mismatches surface while the driver is still on shift, not at month end.' },
    ],
    faq: [
      { question: 'How does the driver record cash collections?', answer: 'When a COD order is marked delivered, the app automatically records the amount. Drivers can also manually adjust if needed.' },
      { question: 'Can I set cash-in-hand limits?', answer: 'Yes. Set maximum cash limits per driver. The system alerts dispatchers and can pause new COD assignments when limits are reached.' },
      { question: 'Does it integrate with accounting software?', answer: 'Yes. Export to Tally, QuickBooks, Zoho Books, and others via CSV or API integration.' },
      { question: 'What happens when collected cash does not match the orders?', answer: 'The shift report lists the discrepancy per driver with the orders behind it, and an alert goes to the dispatcher. Deposits and adjustments are recorded with an audit trail.' },
      { question: 'Can cash be recorded without mobile signal?', answer: 'Yes. The payment step works offline in the driver app and syncs when the device reconnects, so balances catch up automatically.' },
      { question: 'Which plan includes cash management?', answer: 'Cash on delivery management is included on every plan: Geofleet Lite, and Geofleet Command from Professional.' },
    ],
    relatedPosts: [
      'cash-on-delivery-cod-without-headaches-complete-operations-guide',
      'reverse-logistics-returns-pickup-windows-cod',
      'captain-payouts-and-shipper-rate-charts-automate-driver-pay-and-billing',
    ],
  },

  'reports-analytics': {
    slug: 'reports-analytics',
    icon: 'ri-bar-chart-box-line',
    name: 'Reports & Analytics',
    accentColor: '#0EA5E9',
    accentColorLight: '#E0F2FE',
    plan: 'Lite',
    tagline: 'AI-Powered Insights',
    headline: 'Turn delivery data into',
    headlineHighlight: 'actionable intelligence',
    description: 'AI-powered dashboards that surface hidden inefficiencies, predict demand, and recommend operational improvements — transforming raw data into decisions.',
    heroStats: [
      { value: '50+', label: 'Built-in reports' },
      { value: 'Real-time', label: 'Data refresh' },
      { value: 'Copilot', label: 'Answers in plain language' },
    ],
    heroImage: {
      src: featureImg(12),
      alt: 'Geofleet analytics dashboard with on-time rate, orders today, active drivers, cost per delivery, order status, a 30-day trend, deliveries per hub and a driver leaderboard',
      width: 1672,
      height: 941,
    },
    beforeAfter: {
      before: [
        'The weekly ops review runs on a spreadsheet someone built from three exports on Friday night.',
        'On-time rate is a number nobody fully trusts because it depends on who pulled it.',
        'A cost spike in one hub is found a month later, when the fuel bill arrives.',
        'Driver performance conversations rely on memory and complaints.',
      ],
      after: [
        'Dashboards update in real time from the same data dispatch works from.',
        'On-time, cost per delivery, stop time and attendance are defined once and shared.',
        'Anomalies in cost, lateness or driver behaviour are flagged as they happen.',
        'Driver scorecards and hub comparisons are ready for every one-to-one.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'Ask',
        title: 'Ask Copilot for the number instead of building the report',
        body: 'Geofleet Copilot sits next to the live operations view. Ask which stops are running late, why a route is behind or how a hub compares to last week, and get the answer with the orders behind it. The same data feeds every dashboard.',
        bullets: [
          'Plain-language questions over live orders, drivers and routes',
          'Answers link to the orders, trips and drivers involved',
          'Suggested follow-ups such as reassignment, within your permissions',
        ],
        image: {
          src: featureImg(10),
          alt: 'Geofleet live operations view with Copilot answering which stops are at risk this afternoon, listing three late orders with a Reassign button each',
          width: 1586,
          height: 992,
        },
      },
      {
        eyebrow: 'Customers',
        title: 'Delivery feedback, categorised and trended',
        body: 'Ratings and comments from the tracking page are grouped by delivery quality, timeliness, driver service, tracking and communication. See where the experience is slipping before it shows up in reviews.',
        bullets: [
          'Positive and negative share per category, over any date range',
          'Recent feedback with the order and driver attached',
          'Feed low ratings into driver coaching and route changes',
        ],
        image: {
          src: featureImg(11),
          alt: 'Geofleet customer feedback report with positive, neutral and negative share by category, a 12-week rating trend and recent comments',
          width: 1672,
          height: 941,
        },
      },
      {
        eyebrow: 'Share',
        title: 'Scheduled reports and a data API for your BI tools',
        body: 'Daily, weekly or monthly reports go out by email to the people who need them. For deeper analysis, pull the same data into Tableau, Power BI, Looker or Google Sheets through the API.',
        bullets: [
          'Scheduled email reports with your chosen metrics',
          'Custom report builder for views the built-in reports do not cover',
          'Data export API for BI tools and warehouses',
        ],
        image: {
          src: featureImg(9),
          alt: 'Geofleet scheduled reports page listing daily, weekly and monthly reports with recipients and next send time, and integration tiles for Tableau, Power BI, Looker, Google Sheets and an API key',
          width: 1672,
          height: 941,
        },
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-database-2-line', title: 'Data flows in', description: 'Every order, route, driver action, and customer interaction is automatically captured.' },
      { step: '2', icon: 'ri-brain-line', title: 'AI analyzes patterns', description: 'Machine learning surfaces trends, anomalies, and optimization opportunities.' },
      { step: '3', icon: 'ri-lightbulb-line', title: 'Get recommendations', description: 'AI delivers actionable insights — what to fix, where to invest, and what to automate.' },
    ],
    capabilities: [
      { icon: 'ri-dashboard-3-line', title: 'Real-time dashboards', description: 'Live operational views showing deliveries, drivers, costs, and customer metrics.' },
      { icon: 'ri-brain-line', title: 'AI anomaly detection', description: 'Automatically flags unusual patterns — late deliveries, cost spikes, driver issues.', tag: 'AI' },
      { icon: 'ri-line-chart-line', title: 'Demand forecasting', description: 'Predict order volumes by time, day, and location to optimize staffing.', tag: 'AI' },
      { icon: 'ri-user-star-line', title: 'Driver scorecards', description: 'Performance metrics per driver — on-time rate, ratings, speed, and efficiency.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Cost analytics', description: 'Cost-per-delivery, cost-per-mile, and carrier cost comparisons.' },
      { icon: 'ri-map-2-line', title: 'Delivery heat maps', description: 'Visualize delivery density and identify high-demand zones.' },
      { icon: 'ri-chat-smile-3-line', title: 'Copilot', description: 'Ask questions about live operations in plain language and get answers with the orders behind them.', tag: 'AI' },
      { icon: 'ri-file-download-line', title: 'Scheduled reports', description: 'Auto-generate and email daily, weekly, or monthly reports to stakeholders.' },
      { icon: 'ri-code-s-slash-line', title: 'Data export API', description: 'Pull data into your BI tools — Tableau, Power BI, Looker, or custom dashboards.' },
    ],
    useCases: [
      { industry: 'contract-logistics', title: 'Reporting per client', description: 'SLA, cost and proof reports for each client you serve, from one fleet.' },
      { industry: 'grocery', title: 'Slot performance by store', description: 'On-time rate and stop time per store and per slot, compared across the estate.' },
      { industry: 'field-service', title: 'Technician utilisation', description: 'Jobs per day, travel time and first-time fix across teams and regions.' },
    ],
    benefits: [
      { value: 'Real-time', label: 'Instead of Friday night', description: 'Dashboards refresh from live operations, so the review is about this week, not last.' },
      { value: 'One', label: 'Definition of on-time', description: 'Metrics are defined once and shared across dispatch, finance and the client.' },
      { value: '50+', label: 'Reports out of the box', description: 'Driver, hub, cost, customer and attendance reports without building anything.' },
    ],
    integrations: ['Tableau', 'Power BI', 'Looker', 'Google Sheets', 'Slack', 'Email'],
    faq: [
      { question: 'How often is data refreshed?', answer: 'Dashboards update in real-time. Historical reports are available from your first day on the platform.' },
      { question: 'Can I build custom reports?', answer: 'Yes. Use our report builder to create custom views, or export data to your preferred BI tool via API.' },
      { question: 'Is historical data available?', answer: 'All data is retained for the life of your account. Enterprise plans offer up to 7 years of historical data.' },
      { question: 'What can I ask Copilot?', answer: 'Anything about live or past operations: which stops are late and why, how a driver or hub compares to last week, or where cost per delivery is rising. Answers come with the orders and trips behind them.' },
      { question: 'Can reports be sent to people without a Geofleet login?', answer: 'Yes. Scheduled reports are emailed as attachments to any address you choose, on a daily, weekly or monthly cadence.' },
      { question: 'Which plan includes reports and analytics?', answer: 'Reporting is included on every plan, including Geofleet Lite. On Geofleet Command, AI delivery insights are included from Premium AI, and custom reporting and the multi-location dashboard are part of Enterprise.' },
    ],
    relatedPosts: [
      'delivery-kpis-that-actually-matter-for-efficiency-and-growth',
      'fleet-visibility-control-tower-for-logistics-operations',
      'sustainability-metrics-without-breaking-routing',
    ],
  },

  'driver-app': {
    slug: 'driver-app',
    icon: 'ri-smartphone-line',
    name: 'Driver App',
    accentColor: '#1769ff',
    accentColorLight: '#eef5ff',
    plan: 'Lite',
    tagline: 'Driver Mobile App',
    headline: 'The driver app that works',
    headlineHighlight: 'everywhere — even offline',
    description: 'Geofleet’s driver mobile app gives your team real-time tracking, route optimization, electronic proof of delivery (ePOD), e-signatures, OTP verification, attendance with breaks, and Geofleet AI — so drivers stay productive in any language, with or without connectivity.',
    heroStats: [
      { value: 'Offline', label: 'Works without network' },
      { value: 'Multi', label: 'Languages supported' },
      { value: 'Voice', label: 'Hands-free AI assistant' },
    ],
    heroImage: {
      src: featureImg(4),
      alt: 'Three Geofleet driver app screens side by side: the home screen with shift timer and current trip, the delivery review step, and the AI assistant',
      width: 1586,
      height: 992,
    },
    beforeAfter: {
      before: [
        'Drivers get the day’s stops on WhatsApp and navigate from a screenshot.',
        'Attendance is a paper sheet at the hub, reconciled for payroll at month end.',
        'A dead zone on the route means proof is “done later”, if at all.',
        'New drivers take a week to learn the process, longer if they do not read the language the manual is in.',
      ],
      after: [
        'The trip arrives in the app with stops, navigation and the proof each stop needs.',
        'Check-in, check-out and breaks are recorded in the app, ready for payroll.',
        'Everything works offline and syncs when the signal returns.',
        'Geofleet AI answers “how do I…” in the driver’s language, by voice if they prefer.',
      ],
    },
    spotlights: [
      {
        eyebrow: 'Start the shift',
        title: 'Check in, see the trip, start the route',
        body: 'The home screen shows the shift, today’s attendance and the current trip. Drivers check in with one tap, see their stops, distance and expected duration, and start navigation. Shift and break times flow straight into attendance reports.',
        bullets: [
          'Check-in and check-out with on-time and late flags',
          'Break tracking and total working days for payroll',
          'Current trip with stop count, distance and planned times',
        ],
        image: {
          src: featureImg(3),
          alt: 'Driver app home screen with the shift timer, checked-in status, today’s attendance and the current trip with a Continue button',
          width: 941,
          height: 1672,
        },
        fit: 'contain',
      },
      {
        eyebrow: 'Complete the stop',
        title: 'One delivery flow: load, payment, proofs, review',
        body: 'Every stop walks the driver through the same steps. Capture ePOD photos, upload documents and collect an e-signature, then confirm with the customer’s OTP when the order needs a named recipient. Nothing is skipped and nothing lives in the camera roll.',
        bullets: [
          'ePOD photos and document upload at the stop',
          'E-signature on the device with the signed-by name',
          'OTP sent to the customer, entered by the driver to close the stop',
        ],
        image: {
          src: featureImg(2),
          alt: 'Driver app Review step summarising the order, customer, payment collected, proof photos, captured signature and OTP verified before Mark delivered',
          width: 941,
          height: 1672,
        },
        fit: 'contain',
      },
      {
        eyebrow: 'Get help',
        title: 'Geofleet AI, in the driver’s language, hands-free',
        body: 'An in-app assistant explains the next step, summarises the day and can drive the app by voice. Drivers chat with their manager from the same place, and the assistant works in the language the driver chose.',
        bullets: [
          'Learn the app and get day summaries from the assistant',
          'Voice control for drivers who should not be typing',
          'Multi-lingual app and assistant, plus chat with the manager',
        ],
        image: {
          src: featureImg(1),
          alt: 'Driver app AI assistant announcing the next stop and ETA, with voice listening active and a Chat with manager tab',
          width: 941,
          height: 1672,
        },
        fit: 'contain',
      },
    ],
    howItWorks: [
      { step: '1', icon: 'ri-download-cloud-line', title: 'Check in & sync', description: 'Drivers check in from the app; shifts, routes, and trip data sync even when offline.' },
      { step: '2', icon: 'ri-map-pin-range-line', title: 'Run deliveries', description: 'Follow optimized routes, capture ePOD photos and e-signatures, and verify with OTP.' },
      { step: '3', icon: 'ri-upload-cloud-line', title: 'Sync & report', description: 'When back online, data syncs automatically. View analytics and leaderboards in the dashboard.' },
    ],
    capabilities: [
      { icon: 'ri-wifi-off-line', title: 'Offline capability', description: 'View routes, complete deliveries, and capture ePOD and signatures without connectivity; sync when back online.' },
      { icon: 'ri-user-search-line', title: 'Attendance management', description: 'Check-in/check-out, break tracking, and full attendance history for payroll and compliance.' },
      { icon: 'ri-camera-line', title: 'ePOD & document upload', description: 'Photo proof of delivery, document uploads, and structured delivery workflow (Load → Payment → Proofs → Review).' },
      { icon: 'ri-quill-pen-line', title: 'E-signature', description: 'Capture customer e-signatures on device for instant proof of delivery and dispute resolution.' },
      { icon: 'ri-shield-keyhole-line', title: 'OTP verification', description: 'One-time password sent to customer; driver enters OTP to confirm handover and complete payment step.' },
      { icon: 'ri-robot-line', title: 'Geofleet AI', description: 'Learn the app, get summaries, and use the app with voice. In-app assistant and chat with manager.', tag: 'AI' },
      { icon: 'ri-translate-2', title: 'Multi-lingual', description: 'App and assistant available in multiple languages so every driver can work in their preferred language.' },
      { icon: 'ri-money-dollar-circle-line', title: 'COD management', description: 'Track cash-on-delivery payments, collect and reconcile COD with trip and delivery data.' },
      { icon: 'ri-bar-chart-box-line', title: 'Analytics', description: 'Driver and trip analytics, performance metrics, and insights right in the app and dashboard.' },
      { icon: 'ri-trophy-line', title: 'Leaderboard', description: 'Gamified leaderboards to recognize top performers and encourage consistency across the fleet.' },
    ],
    useCases: [
      { industry: 'couriers', title: 'High-volume parcel runs', description: 'Dozens of stops a day with scans, photos and signatures that must not slow the driver down.' },
      { industry: 'water', title: 'Routes through dead zones', description: 'Rural and basement deliveries where the app has to work without signal.' },
      { industry: 'laundry', title: 'Pickup and return in one run', description: 'Mixed pickups and drops with different proof at each, on a route that repeats weekly.' },
    ],
    benefits: [
      { value: 'Offline', label: 'No connectivity required', description: 'Drivers complete deliveries and capture proof even in low-signal or offline areas.' },
      { value: '1 app', label: 'All-in-one', description: 'Attendance, ePOD, e-sign, OTP, COD, and AI assistant in a single driver experience.' },
      { value: 'Voice', label: 'Hands-free with AI', description: 'Geofleet AI helps drivers learn and use the app with voice and multi-lingual support.' },
    ],
    integrations: ['Geofleet Web Dashboard', 'Route Planner', 'Reports & Analytics', 'WhatsApp', 'SMS'],
    faq: [
      { question: 'Does the driver app work offline?', answer: 'Yes. Drivers can view assigned routes, complete delivery steps, capture photos and e-signatures, and record attendance. Data syncs automatically when the device is back online.' },
      { question: 'What is Geofleet AI in the driver app?', answer: 'Geofleet AI helps drivers learn the app, get summaries, and use features with voice. It includes an in-app assistant and chat with manager, with multi-lingual support.' },
      { question: 'How does OTP verification work?', answer: 'An OTP is sent to the customer via email or SMS. The driver enters the code in the app to confirm handover and proceed with the payment step, reducing fraud and failed deliveries.' },
      { question: 'Is the app available on iOS and Android?', answer: 'Yes. The driver app is available for both iOS and Android. Drivers sign in with the account your dispatcher creates for them.' },
      { question: 'Can the app be branded with our logo?', answer: 'Yes. A branded driver app is included from the Premium plan.' },
      { question: 'Which plan includes the driver app?', answer: 'The driver mobile app is included on every plan. Geofleet Lite starts at $49 a month with 3 drivers included, and extra drivers cost $8 to $10 a month each depending on the plan.' },
    ],
    relatedPosts: [
      'best-driver-tracking-apps-delivery-teams',
      'ai-assisted-onboarding-first-login-to-live-dispatch',
      'proof-of-delivery-legal-cx-infrastructure',
    ],
  },
};
